import { useState, FormEvent } from 'react';

export default function ApplicationForm() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    timeAvailability: '',
    availability: '',
    networkingExperience: '',
    socialMediaActivity: '',
    mainPlatforms: [] as string[],
    otherPlatform: '',
    selfResponsibility: '5',
    motivation: '',
    expectations: '',
    dataPrivacy: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: target.checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handlePlatformChange = (platform: string) => {
    setFormData(prev => ({
      ...prev,
      mainPlatforms: prev.mainPlatforms.includes(platform)
        ? prev.mainPlatforms.filter(p => p !== platform)
        : [...prev.mainPlatforms, platform]
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!formData.dataPrivacy) {
      alert('Bitte bestätigen Sie die Datenschutzerklärung.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const submitData = new URLSearchParams();
      submitData.append('firstName', formData.firstName);
      submitData.append('lastName', formData.lastName);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('location', formData.location);
      submitData.append('timeAvailability', formData.timeAvailability);
      submitData.append('availability', formData.availability);
      submitData.append('networkingExperience', formData.networkingExperience);
      submitData.append('socialMediaActivity', formData.socialMediaActivity);
      submitData.append('mainPlatforms', formData.mainPlatforms.join(', '));
      if (formData.otherPlatform) {
        submitData.append('otherPlatform', formData.otherPlatform);
      }
      submitData.append('selfResponsibility', formData.selfResponsibility);
      submitData.append('motivation', formData.motivation);
      submitData.append('expectations', formData.expectations);

      const response = await fetch('https://readdy.ai/api/form/d4fdtri2ju15crl22s5g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          location: '',
          timeAvailability: '',
          availability: '',
          networkingExperience: '',
          socialMediaActivity: '',
          mainPlatforms: [],
          otherPlatform: '',
          selfResponsibility: '5',
          motivation: '',
          expectations: '',
          dataPrivacy: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="application-form" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 text-center">
            Bewerbung als freiberuflicher Vertriebspartner
          </h2>
          
          <p className="text-xl text-gray-700 mb-12 text-center">
            Bitte fülle die folgenden Angaben aus – wir melden uns zeitnah zurück und laden dich ggf. zu einem persönlichen Kennenlern-Zoom ein.
          </p>

          <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-2xl shadow-xl" data-readdy-form id="partner-application-form">
            {/* Persönliche Daten */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-blue-600">
                Persönliche Daten
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Vorname <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none text-base"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Nachname <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none text-base"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    E-Mail <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none text-base"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Telefonnummer <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none text-base"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-gray-700 font-semibold mb-2">
                  Wohnort / Land <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none text-base"
                />
              </div>
            </div>

            {/* Zeit & Verfügbarkeit */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-blue-600">
                Zeit & Verfügbarkeit
              </h3>
              
              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-3">
                  Wie viel Zeit kannst du pro Woche investieren? <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  {['5–10 Stunden', '10–20 Stunden', '20+ Stunden', 'Noch unsicher'].map((option) => (
                    <label key={option} className="flex items-center gap-3 p-4 border-2 border-gray-300 rounded-xl hover:border-blue-500 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="timeAvailability"
                        value={option}
                        checked={formData.timeAvailability === option}
                        onChange={handleInputChange}
                        required
                        className="w-5 h-5 text-blue-600 cursor-pointer"
                      />
                      <span className="text-gray-800">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Wann bist du am besten erreichbar? <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="availability"
                  value={formData.availability}
                  onChange={handleInputChange}
                  required
                  maxLength={500}
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none text-base resize-none"
                  placeholder="z.B. Montag bis Freitag 9:00-18:00 Uhr, Samstag 10:00-14:00 Uhr"
                />
                <p className="text-sm text-gray-500 mt-1">
                  {formData.availability.length}/500 Zeichen
                </p>
              </div>
            </div>

            {/* Erfahrungen & Online-Präsenz */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-blue-600">
                Erfahrungen & Online-Präsenz
              </h3>
              
              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-3">
                  Erfahrung im Bereich Networking/Leadgenerierung? <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  {['Viel Erfahrung', 'Etwas Erfahrung', 'Noch keine Erfahrung'].map((option) => (
                    <label key={option} className="flex items-center gap-3 p-4 border-2 border-gray-300 rounded-xl hover:border-blue-500 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="networkingExperience"
                        value={option}
                        checked={formData.networkingExperience === option}
                        onChange={handleInputChange}
                        required
                        className="w-5 h-5 text-blue-600 cursor-pointer"
                      />
                      <span className="text-gray-800">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-3">
                  Wie aktiv bist du auf Social Media? <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  {['Sehr aktiv (täglich Content)', 'Regelmäßig aktiv', 'Gelegentlich aktiv', 'Kaum aktiv'].map((option) => (
                    <label key={option} className="flex items-center gap-3 p-4 border-2 border-gray-300 rounded-xl hover:border-blue-500 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="socialMediaActivity"
                        value={option}
                        checked={formData.socialMediaActivity === option}
                        onChange={handleInputChange}
                        required
                        className="w-5 h-5 text-blue-600 cursor-pointer"
                      />
                      <span className="text-gray-800">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-3">
                  Hauptplattform(en): <span className="text-red-500">*</span>
                </label>
                <div className="grid md:grid-cols-2 gap-3 mb-4">
                  {['Instagram', 'Facebook', 'TikTok', 'LinkedIn'].map((platform) => (
                    <label key={platform} className="flex items-center gap-3 p-4 border-2 border-gray-300 rounded-xl hover:border-blue-500 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.mainPlatforms.includes(platform)}
                        onChange={() => handlePlatformChange(platform)}
                        className="w-5 h-5 text-blue-600 rounded cursor-pointer"
                      />
                      <span className="text-gray-800">{platform}</span>
                    </label>
                  ))}
                </div>
                <input
                  type="text"
                  name="otherPlatform"
                  value={formData.otherPlatform}
                  onChange={handleInputChange}
                  placeholder="Andere Plattform(en)"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none text-base"
                />
              </div>
            </div>

            {/* Arbeitsweise & Motivation */}
            <div className="mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-blue-600">
                Arbeitsweise & Motivation
              </h3>
              
              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-3">
                  Wie wichtig ist dir eigenverantwortliches Arbeiten? (Skala 1–10) <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-4">
                  <span className="text-gray-600">1</span>
                  <input
                    type="range"
                    name="selfResponsibility"
                    min="1"
                    max="10"
                    value={formData.selfResponsibility}
                    onChange={handleInputChange}
                    required
                    className="flex-1 cursor-pointer"
                  />
                  <span className="text-gray-600">10</span>
                  <span className="w-12 h-12 flex items-center justify-center bg-blue-600 text-white text-xl font-bold rounded-full">
                    {formData.selfResponsibility}
                  </span>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-2">
                  Was motiviert dich an dieser Chance? <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="motivation"
                  value={formData.motivation}
                  onChange={handleInputChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none text-base resize-none"
                  placeholder="Teile uns deine Motivation mit..."
                />
                <p className="text-sm text-gray-500 mt-1">
                  {formData.motivation.length}/500 Zeichen
                </p>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Was erwartest du von der Zusammenarbeit? <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="expectations"
                  value={formData.expectations}
                  onChange={handleInputChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none text-base resize-none"
                  placeholder="Beschreibe deine Erwartungen..."
                />
                <p className="text-sm text-gray-500 mt-1">
                  {formData.expectations.length}/500 Zeichen
                </p>
              </div>
            </div>

            {/* Abschluss & Datenschutz */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-blue-600">
                Datenschutz
              </h3>
              
              <div className="space-y-4">
                <label className="flex items-start gap-3 p-4 border-2 border-gray-300 rounded-xl hover:border-blue-500 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    name="dataPrivacy"
                    checked={formData.dataPrivacy}
                    onChange={handleInputChange}
                    required
                    className="w-5 h-5 text-blue-600 rounded mt-1 cursor-pointer flex-shrink-0"
                  />
                  <span className="text-gray-800">
                    Ich stimme der Verarbeitung meiner Daten im Rahmen des Bewerbungsprozesses zu.
                  </span>
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white text-xl font-bold px-12 py-5 rounded-full transition-all duration-300 shadow-xl hover:shadow-blue-500/50 hover:scale-105 disabled:cursor-not-allowed disabled:scale-100 whitespace-nowrap cursor-pointer"
              >
                <span>👉</span>
                <span>{isSubmitting ? 'Wird gesendet...' : 'Bewerbung absenden'}</span>
              </button>
            </div>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="mt-6 p-6 bg-green-50 border-2 border-green-500 rounded-xl text-center">
                <p className="text-green-700 font-semibold text-lg">
                  ✓ Vielen Dank für deine Bewerbung! Wir melden uns zeitnah bei dir.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-6 p-6 bg-red-50 border-2 border-red-500 rounded-xl text-center">
                <p className="text-red-700 font-semibold text-lg">
                  ✗ Es gab einen Fehler beim Absenden. Bitte versuche es erneut.
                </p>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
